import React from 'react';
import Alert from '@material-ui/lab/Alert';
import makeStyles from '@material-ui/core/styles/makeStyles';
import {useSelector} from 'react-redux';
import PropTypes from 'prop-types';
import NoResultPlaceholder from 'Components/common/NoResultsPlaceholder/NoResultPlaceholder';
import {NO_RESULT_PLACEHOLDER} from 'Utils/labels';
import * as analytics from 'Utils/analytics';
import classNames from 'classnames';
import useFilterSelection from 'Utils/useFilterSelection';
import EventCard from './EventCard';

const useStyles = makeStyles(() => ({
    warning: {
        backgroundColor: '#ECB934',
        color: '#3E3E3E',
        margin: '0 16px',
        borderRadius: '8px',
        fontSize: '12px',
        padding: '0px 16px',
    },
    warningIcon: {
        color: 'black !important',
    },
}));

export const NOW_ON = 'NOW ON';
const UPCOMING = 'UPCOMING';

const EventsList = ({
    showHeader,
    directoryLocation,
    events,
    isExperiences,
    isEventDetails,
    isConnectedExperiences,
    isDsm,
    isMainMenuEventList,
    navigate,
    setJourneyStop,
    setSearchLocation,
    setTakeMeThereEventId,
    isTabView,
    setClearFilterHandler,
}) => {
    const classes = useStyles();
    const isExplorer = useSelector(state => state.pageState.layout.isExplorer);
    const {
        isFilterSelected,
        isNoFilterSelected,
        toggleFilter,
        unselectAllFilters,
    } = useFilterSelection();

    if (setClearFilterHandler) {
        setClearFilterHandler(unselectAllFilters);
    }

    const menuColourExperiences = useSelector(
        state =>
            state.pageState.layout.config.signConfig.withInheritance.menuTab?.menuColourExperiences
    );

    const menuLabelExperiences = useSelector(
        state =>
            state.pageState.layout.config.signConfig.withInheritance.menuTab.menuLabelExperiences
    );

    const primaryColors = useSelector(
        state => state.pageState.layout.config.settings.brandingConfig.primary.colors
    );

    const filteredEvents = events.filter(event => {
        if (isNoFilterSelected()) {
            return true;
        }
        const startInTheFuture = event.displayTime !== NOW_ON;
        if (startInTheFuture) {
            return isFilterSelected(UPCOMING);
        }
        return isFilterSelected(NOW_ON);
    });

    const emptyResults = () => {
        if (isDsm) {
            return (
                <Alert
                    classes={{standardWarning: classes.warning, icon: classes.warningIcon}}
                    severity="warning"
                >
                    Warning: This screen/sign has no experiences assigned to it on this scene,
                    therefore the screen will appear empty. You need to assign experiences to screen
                    in Experiences Manager.
                </Alert>
            );
        }
        return (
            <div id="kiosk-search">
                <div id="kiosk-search-results">
                    <NoResultPlaceholder
                        {...NO_RESULT_PLACEHOLDER.event}
                        backgroundColor={menuColourExperiences}
                    />
                </div>
            </div>
        );
    };

    const eventList = filteredEvents.map(event => (
        <EventCard
            key={event.id}
            event={event}
            isDsm={isDsm}
            selectEvent={e => {
                const analyticsExtraFields = {
                    EventId: event.id,
                    EventName: event.name,
                };

                if (isExperiences) {
                    analytics.sendSearchEvent(
                        analytics.ACTIONS.LOCATION,
                        'Event',
                        analyticsExtraFields
                    );
                } else {
                    analytics.sendCampaignEventsEvent(
                        analytics.ACTIONS.EVENT,
                        'Select',
                        analyticsExtraFields
                    );
                }
                if (e.feature) {
                    setSearchLocation({feature: e, fitBounds: true});
                }
                setTakeMeThereEventId(null);
                navigate('eventDetails', {item: e, saveSearchState: true});
            }}
            setJourneyStop={setJourneyStop}
        />
    ));

    let content;

    if (isExplorer && !isEventDetails && eventList.length > 0) {
        content = (
            <div
                style={{
                    overflowY: 'scroll',
                    height: 'inherit',
                }}
            >
                {eventList}
            </div>
        );
    } else {
        content = eventList;
    }

    const filters = [NOW_ON, UPCOMING].map(filter => {
        return (
            <div
                key={filter}
                className={classNames('pill', {
                    active: isFilterSelected(filter),
                })}
                style={
                    isFilterSelected(filter)
                        ? {
                              color: primaryColors.text,
                              backgroundColor: primaryColors.background,
                          }
                        : {}
                }
                onClick={() => {
                    toggleFilter(filter);
                }}
            >
                {filter}
            </div>
        );
    });

    if (isConnectedExperiences && events.length === 0) {
        return null;
    }

    return (
        <>
            {!showHeader &&
                !isTabView && (
                    <div>
                        {isConnectedExperiences ? (
                            <h5 className="connected-experiences">{`Connected ${menuLabelExperiences}`}</h5>
                        ) : (
                            <h4>{menuLabelExperiences}</h4>
                        )}
                    </div>
                )}
            <div className="content-container">
                <div className="content-stack">
                    {isExperiences &&
                        showHeader && (
                            <div className="content-header">
                                <div>
                                    <p>{directoryLocation}</p>
                                </div>

                                <div className="list-filters" data-testid="promotions-filters">
                                    {filters}
                                </div>
                            </div>
                        )}
                    <div
                        className={classNames('content-grow', 'p-8', 'content-body', {
                            'kiosk-content-body': !isExplorer,
                            'grey-bg': !isMainMenuEventList,
                            'm-b-0': !isMainMenuEventList && isExplorer,
                        })}
                    >
                        <div
                            data-testid="experiences-list"
                            className={classNames('card-list experiences-list', {
                                'kiosk-card-list': !isExplorer,
                            })}
                        >
                            {eventList.length > 0 ? content : emptyResults()}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
EventsList.defaultProps = {
    isTabView: false,
};

EventsList.propTypes = {
    showHeader: PropTypes.bool,
    isTabView: PropTypes.bool,
    directoryLocation: PropTypes.string,
    events: PropTypes.array,
    isExperiences: PropTypes.bool,
    isEventDetails: PropTypes.bool,
    isConnectedExperiences: PropTypes.bool,
    isDsm: PropTypes.bool,
    isMainMenuEventList: PropTypes.bool,
    navigate: PropTypes.func.isRequired,
    setJourneyStop: PropTypes.func.isRequired,
    setSearchLocation: PropTypes.func.isRequired,
    setTakeMeThereEventId: PropTypes.func.isRequired,
    setClearFilterHandler: PropTypes.func,
};

export default EventsList;
